import React, { useState, useMemo, useEffect } from 'react';
import { 
  FormControl, 
  Select, 
  MenuItem, 
  Checkbox, 
  Box, 
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Chip
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  useFetchAllDepartmentsQuery,
  useLazyGetDepartmentUsersQuery,
  useGenerateWorkforceReportMutation,
} from '../../slice/logdetailSlice';
import dayjs from 'dayjs';
import { toast } from "react-toastify";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const WorkforceStability = () => {
  const [duration, setDuration] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [openTeamDD, setOpenTeamDD] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [showTable, setShowTable] = useState(false);
  const [hasRunReport, setHasRunReport] = useState(false); // Track if report has been run

  /* ================= Departments ================= */
  const { data: rawDepartments = [], refetch: refetchDepartments } = useFetchAllDepartmentsQuery();

  const allDepartments = useMemo(() => {
    return (rawDepartments || []).filter(dept => 
      dept.department_name && dept.department_name.trim().toLowerCase() !== 'all'
    );
  }, [rawDepartments]);

  
  useEffect(() => {
    refetchDepartments();
  }, [refetchDepartments]);

  /* ================= Selected Department IDs ================= */
 /* ================= Selected Department IDs ================= */
const departmentIds = useMemo(() => {
  if (selectedDepartments.length === 0) return [];
  
  if (selectedDepartments.includes('All')) {
    return allDepartments.map(d => d.department_id).filter(Boolean);
  }
  
  return selectedDepartments
    .map(name => {
      const dept = allDepartments.find(d => d.department_name === name);
      return dept?.department_id;
    })
    .filter(id => id !== undefined && id !== null); // Remove undefined and null values
}, [selectedDepartments, allDepartments]);

  /* ================= API Queries ================= */
  const [getDepartmentUsers, { 
    data: teamMembersRes, 
    isFetching: isFetchingTeamMembers,
    isLoading: isLoadingTeamMembers,
    error: teamMembersError 
  }] = useLazyGetDepartmentUsersQuery();

  const [generateReport, { 
    isFetching: isFetchingReport,
    isLoading: isLoadingReport,
    error: reportError 
  }] = useGenerateWorkforceReportMutation();

  const teamMembers = teamMembersRes?.data || [];

  const handleTeamDropdownOpen = () => {
  // Check if departmentIds has at least one valid ID
  const validDepartmentIds = departmentIds.filter(id => id !== null && id !== undefined);
  
  if (validDepartmentIds.length > 0 && !openTeamDD) {
    getDepartmentUsers({ department_ids: validDepartmentIds });
  }
  setOpenTeamDD(true);
};

  const getDurationType = (durationValue) => {
    const mapping = {
      'Last 30 days': '30days',
      'Last 60 days': '60days',
      'Last 90 days': '90days',
      'Last month': 'last_month',
      'This year': 'this_year',
      'Custom': 'custom'
    };
    return mapping[durationValue] || 'custom';
  };

  // Helper function to format date as dd-mm-yyyy
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const date = dayjs(dateString);
    if (!date.isValid()) return '';
    return date.format('DD-MM-YYYY');
  };

  /* ================= Handle Snackbar Close ================= */
  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  /* ================= Format Table Data ================= */
  const formatTableData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }
    
    // Format dates for display
    const formattedFromDate = fromDate ? fromDate.format('DD-MM-YYYY') : '';
    const formattedToDate = toDate ? toDate.format('DD-MM-YYYY') : '';
    
    return apiData.map((item, index) => ({
      id: item.user_id || index + 1,
      employeeName: item.user_name || `User ${index + 1}`,
      department: item.department_name || '',
      longTerm: item.long_term_percent !== undefined ? `${item.long_term_percent}%` : '0%',
      engaged: item.engaged_percent !== undefined ? `${item.engaged_percent}%` : '0%',
      shortTermAvailable: item.shortly_available_percent !== undefined ? `${item.shortly_available_percent}%` : '0%',
      bench: item.bench_percent !== undefined ? `${item.bench_percent}%` : '0%',
      stabilityScore: item.stability_score !== undefined ? `${item.stability_score}` : '0',
      // Add formatted date fields for custom duration
      fromDate: formattedFromDate,
      toDate: formattedToDate
    }));
  };

  /* ================= Run Report Handler ================= */
  const handleRunReport = async () => {
    // Validate required fields
    if (!duration) {
      toast.error('Please select a mandatory fields');
      return;
    }

    if (selectedDepartments.length === 0) {
      toast.error('Please select a mandatory fields');
      return;
    }

    if (duration === 'Custom') {
      if (!fromDate || !toDate) {
        toast.error('Please select a mandatory fields');
        return;
      }

      // Check invalid date
      if (!fromDate.isValid() || !toDate.isValid() || fromDate.isAfter(toDate)) {
        toast.error('Please select valid Date Range');
        return;
      }
    }

    // Get user IDs
    const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
    const userIds = selectedTeamMembers.length > 0
      ? safeTeamMembers
          .filter(member => selectedTeamMembers.includes(member.name))
          .map(member => member.user_id)
      : [];

    try {
      // Format dates for API payload (yyyy-mm-dd format for API)
      const apiFromDate = fromDate ? fromDate.format('YYYY-MM-DD') : '';
      const apiToDate = toDate ? toDate.format('YYYY-MM-DD') : '';
      
      const payload = {
        user_ids: userIds,
        department_ids: departmentIds,
        from_date: apiFromDate,
        to_date: apiToDate,
        duration_type: getDurationType(duration),
        limit: paginationModel.pageSize,
        offset: paginationModel.page + 1
      };

      // console.log("Sending API request:", payload);

      const response = await generateReport(payload).unwrap();
      // console.log("API Response:", response);

      // Handle different response formats
      if (response.status === false) {
        // When API returns status: false with message "No data found"
        setTableData([]);
        toast.info('No data found for the selected criteria');
        setShowTable(true);
        setHasRunReport(true); // Mark that report has been run
      } else if (response.status === true && Array.isArray(response.data)) {
        const formattedData = formatTableData(response.data);
        setTableData(formattedData);
        toast.success(`${formattedData.length} records generated successfully`);
        setShowTable(true);
        setError(null);
        setHasRunReport(true); // Mark that report has been run
      } else if (Array.isArray(response)) {
        const formattedData = formatTableData(response);
        setTableData(formattedData);
        toast.success(`${formattedData.length} records generated successfully`);
        setShowTable(true);
        setError(null);
        setHasRunReport(true); // Mark that report has been run
      } else if (response.data && Array.isArray(response.data)) {
        const formattedData = formatTableData(response.data);
        setTableData(formattedData);
        toast.success(`${formattedData.length} records generated successfully`);
        setShowTable(true);
        setError(null);
        setHasRunReport(true); // Mark that report has been run
      } else {
        setTableData([]);
        toast.info('Unexpected response format from server');
        setShowTable(true);
        setHasRunReport(true); // Mark that report has been run
      }
    } catch (error) {
      console.error("Error generating report:", error);
      setTableData([]);
      toast.error(error.data?.message || 'No data available.');
      setShowTable(true);
      setHasRunReport(true); // Mark that report has been run
    }
  };

  // Handle changes in filters - DON'T clear table data
  const handleDurationChange = (e) => {
    setDuration(e.target.value);
    // Remove table clearing logic - table should stay visible
    if (e.target.value !== 'Custom') {
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleDepartmentsChange = (e) => {
  const value = e.target.value;
  const isAllPreviouslySelected = selectedDepartments.includes('All');
  const isAllCurrentlySelected = value.includes('All');

  if (isAllCurrentlySelected && !isAllPreviouslySelected) {
    // "All" was newly selected -> Select everything
    setSelectedDepartments(['All', ...allDepartments.map(d => d.department_name)]);
  } else if (!isAllCurrentlySelected && isAllPreviouslySelected) {
    // "All" was newly deselected -> Uncheck everything
    setSelectedDepartments([]);
  } else {
    // Individual selection or "All" was already selected and another item was toggled
    const newValue = value.filter(v => v !== 'All');
    setSelectedDepartments(newValue);
  }
  
  setSelectedTeamMembers([]);
  setOpenTeamDD(false);
};

  const handleTeamMembersChange = (event, newValue) => {
    setSelectedTeamMembers(newValue);
    // DON'T clear table data here - only clear when Run Report is clicked
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    // Remove table clearing logic - table should stay visible
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue);
    // Remove table clearing logic - table should stay visible
  };

  // Define base columns
  const baseColumns = [
    { 
      field: 'employeeName', 
      headerName: 'Employee Name', 
      flex: 1, 
      minWidth: 150,
    },
    { 
      field: 'department', 
      headerName: 'Department', 
      flex: 1, 
      minWidth: 120,
    },
  ];

  // Add date columns when Custom duration is selected
  const dateColumns = duration === 'Custom' ? [
    { 
      field: 'fromDate', 
      headerName: 'From Date', 
      flex: 0.8, 
      minWidth: 100,
      valueFormatter: (params) => params.value || ''
    },
    { 
      field: 'toDate', 
      headerName: 'To Date', 
      flex: 0.8, 
      minWidth: 100,
      valueFormatter: (params) => params.value || ''
    },
  ] : [];

  // Define remaining columns
  const remainingColumns = [
    { 
      field: 'longTerm', 
      headerName: '% Long-term', 
      flex: 0.8, 
      minWidth: 100,
    },
    { 
      field: 'engaged', 
      headerName: '% Engaged', 
      flex: 0.8, 
      minWidth: 100,
    },
    { 
      field: 'shortTermAvailable', 
      headerName: '% Shortly Available', 
      flex: 1, 
      minWidth: 130,
    },
    { 
      field: 'bench', 
      headerName: '% Bench', 
      flex: 0.8, 
      minWidth: 80,
    },
    { 
      field: 'stabilityScore', 
      headerName: 'Stability Score', 
      flex: 1, 
      minWidth: 120,
    },
  ];

  // Combine all columns
  const columns = [...baseColumns, ...remainingColumns];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="container-fluid bg-white p-3">
        {/* Snackbars for notifications */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        {/* First Row: Duration and Departments - ALWAYS VISIBLE */}
        <div className="row align-items-start mb-3">
          <div className="col-md-5">
            <div className="d-flex align-items-center flex-wrap">
              <label className="duration-field form-label mb-0 me-3" style={{ minWidth: '80px' }}>Duration
                <span className="text-rgbr-red"> *</span>
              </label>
              <FormControl size="small" sx={{ flex: 1, maxWidth: '400px' }}>
                <Select
                  value={duration}
                  onChange={handleDurationChange}
                  displayEmpty
                  renderValue={(val) => val || "Select Duration"}
                >
                  {['Last 30 days', 'Last 60 days', 'Last 90 days', 'Last month', 'This year', 'Custom'].map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="col-md-7">
            <div className="d-flex align-items-center">
              <label className="department-field form-label mb-0 me-3" style={{ minWidth: '100px' }}>Departments
                <span className="text-rgbr-red"> *</span>
              </label>
              <div className="d-flex align-items-center" style={{ flex: 1, maxWidth: '500px' }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    multiple
                    value={selectedDepartments}
                    onChange={handleDepartmentsChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) return "Select Departments";
                      if (selected.includes('All')) return "All Departments";
                      return selected.join(", ");
                    }}
                  >
                    <MenuItem value="All">
                      <Checkbox checked={selectedDepartments.includes('All')} />
                      All
                    </MenuItem>
                    {allDepartments?.map(dept => (
                      <MenuItem key={dept.department_id} value={dept.department_name}>
                        <Checkbox checked={selectedDepartments.includes(dept.department_name) || selectedDepartments.includes('All')} />
                        {dept.department_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <button 
                  className="btn btn-primary ms-3 AddButton p-2 border border-none" 
                  style={{ minWidth: '120px', height: '40px', flexShrink: 0 }}
                  // REMOVED: disabled condition - button always enabled
                  onClick={handleRunReport}
                >
                  {(isFetchingReport || isLoadingReport) ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                      Loading...
                    </>
                  ) : 'Run Report'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Row - PRESERVE ORIGINAL UI */}
        <div className="row align-items-start">
          {duration === 'Custom' && (
            <div className="col-md-5">
              <div className="d-flex align-items-center flex-wrap">
                <label className="date-range-field form-label mb-0 me-3" style={{ minWidth: '80px' }}>Date Range
                  <span className="text-rgbr-red"> *</span></label>
                <Box sx={{ display: 'flex', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                  <DatePicker 
                    label="From" 
                    value={fromDate} 
                    onChange={handleFromDateChange}
                    format='DD/MM/YYYY'
                    maxDate={toDate || dayjs()} 
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: '195px' }
                      }
                    }} 
                  />
                
                  <DatePicker 
                    label="To" 
                    value={toDate} 
                    onChange={handleToDateChange}
                    format='DD/MM/YYYY'
                    minDate={fromDate}
                    maxDate={dayjs()} 
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: '195px' }
                      }
                    }} 
                  />
                </Box>
              </div>
            </div>
          )}
          
          {selectedDepartments.length > 0 && (
            <div className={duration === 'Custom' ? "col-md-7" : "col-md-7 offset-md-5"}>
              <div className="team-members d-flex align-items-center flex-wrap">
                <label className="form-label mb-0 me-3" style={{ minWidth: '100px' }}>Team Members:</label>
                      <Autocomplete
                           multiple
                           limitTags={2}
                           disableCloseOnSelect
                           open={openTeamDD}
                           onOpen={handleTeamDropdownOpen}
                           onClose={() => setOpenTeamDD(false)}
                           value={selectedTeamMembers}
                           onChange={handleTeamMembersChange}
                           options={teamMembers.map((member) => member.name)}
                           getOptionLabel={(option) => option}
                           loading={isFetchingTeamMembers || isLoadingTeamMembers}
                           disabled={departmentIds.length === 0}
                           renderOption={(props, option, { selected }) => (
                             <li {...props}>
                               <Checkbox
                                 icon={icon}
                                 checkedIcon={checkedIcon}
                                 style={{ marginRight: 8 }}
                                 checked={selected}
                               />
                               {option}
                             </li>
                           )}
                           renderTags={(value, getTagProps) =>
                             value.map((option, index) => (
                               <Chip
                                 label={option}
                                 {...getTagProps({ index })}
                                 deleteIcon={<CancelIcon sx={{ color: 'red' }} />}
                               />
                             ))
                           }
                           renderInput={(params) => (
                             <TextField
                               {...params}
                               variant="outlined"
                               {...(selectedTeamMembers.length === 0 && { placeholder: "Select Team Members" })}
                               size="small"
                             />
                           )}
                           sx={{
                             flex: 1,
                             maxWidth: '500px',
                             '& .MuiAutocomplete-inputRoot': {
                               flexWrap: 'wrap',
                               minHeight: '40px',
                             },
                             '& .MuiChip-root': {
                               margin: '2px',
                             }
                           }}
                         />
              </div>
            </div>
          )}
        </div>

        {/* Table Section - Only add overflowX */}
        {hasRunReport && (
          <div className="row mt-4">
            <div className="col-12">
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                  rows={tableData}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 20, 30, 40]}
                  checkboxSelection={false}
                  disableRowSelectionOnClick
                  loading={isFetchingReport || isLoadingReport}
                  autoHeight
                  slots={{
                    noRowsOverlay: () => (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: '200px',
                          color: 'text.secondary',
                          p: 3
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#a7a5a5',
                            width: '150px',
                            textAlign: 'center',
                            marginBottom: '80px',
                          }}
                        >
                          No data found
                        </Typography>
                      </Box>
                    ),
                  }}
                  sx={{
                    border: 1,
                    borderColor: '#e0e0e0',
                    '& .data-grid-header': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                      backgroundColor: '#fafafa',
                    },
                    '& .MuiDataGrid-cell': {
                      borderRight: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: '2px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e0e0e0',
                    },
                  }}
                />
              </Box>
            </div>
          </div>
        )}

        {/* Show message before running first report */}
        {!hasRunReport && duration && selectedDepartments.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                p={4} 
                sx={{ 
                  borderTop: '1px dashed #fff',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  minHeight: '500px'
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  <span className='text-label form-label'>Click "Run Report" to generate workforce stability data</span>
                </Typography>
              </Box>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default WorkforceStability;